import React, { useCallback } from 'react'
import { Linking } from 'react-native'

import { useModal } from '@zet/common/hooks/useModal'
import { useShareHook } from '@zet/common/hooks/useShareHook'
import {
  B4,
  BTN,
  BTN_SM,
  H6,
  Icon,
  SH2,
  Stack,
  XStack,
  YStack,
  colors,
} from '@zet/design-system'
import { BottomSheet } from '@zet/design-system/bottomSheet'
import Pressable from '@zet/design-system/pressable'
import { useCreditScoreUrl, useUserContext } from '@zet/feature-user'
import { useSpecificAppComms } from '@zet/feature-user/api/appComms/hooks'
import { appVideos } from '@zet/utils/Constants'
import LottieView from 'lottie-react-native'

import { creditScoreLottie } from '../../assets'
import { i18n } from '../../localization'
import { creditScoreInfoListData } from '../utils/componentConstants'

interface ICreditScoreBottomSheet {
  onDismiss: () => void
  visible: boolean
}

const CreditScoreBottomSheet = ({
  onDismiss,
  visible,
}: ICreditScoreBottomSheet) => {
  const { openModal } = useModal()
  const { data } = useCreditScoreUrl()
  const { data: user } = useUserContext()
  const { data: videoData } = useSpecificAppComms('appVideos')

  const link = data?.data?.link
  const message = i18n.t('creditScore.shareDescription', {
    link: link,
    name: user?.fullName,
  })

  const { share: handleShare } = useShareHook(message)

  const openCreditScoreLink = () => {
    if (link) {
      Linking.openURL(link)
    }
  }

  const handlePlayVideo = useCallback(() => {
    onDismiss()
    openModal('YoutubeVideoModal', {
      videoId: videoData?.zetCreditScore || appVideos.zetPlaceHolder.videoId,
      videoData: videoData?.zetCreditScore,
    })
  }, [videoData, openModal])

  return (
    <BottomSheet
      visible={visible}
      disableBottomSheet={onDismiss}
      onDismiss={onDismiss}
      sheetName="homepage_new_credit_score"
    >
      <YStack>
        <YStack className="px-3">
          {/*----Lottie Animation Section----*/}
          <XStack className="justify-center space-x-2">
            <LottieView
              autoPlay
              source={creditScoreLottie}
              style={{ height: 96, width: 96 }}
            />
          </XStack>

          {/*----Header Section----*/}
          <XStack className="justify-center">
            <H6>{i18n.t('checkCreditScore.freeCheck')}</H6>
          </XStack>

          {/*----Check Credit Score Section----*/}
          <Stack className="border-grays-40 mt-4 flex-1 rounded-lg border py-3">
            <SH2 className="text-grays-80 text-center">
              {i18n.t('checkCreditScore.checkScoreTitle')}
            </SH2>
            <Pressable
              className={
                'bg-primary-10 mt-3 w-max self-center rounded px-3 py-2'
              }
              onPress={openCreditScoreLink}
            >
              <BTN_SM className={'text-primary-100'}>
                {i18n.t('checkCreditScore.checkScoreCTA')}
              </BTN_SM>
            </Pressable>
          </Stack>

          {/*----Credit Score Features Section----*/}
          <Stack className="border-grays-40 my-3 flex-1 rounded-lg border">
            <SH2 className="text-grays-80 mx-4 py-3 text-center">
              {i18n.t('checkCreditScore.title')}
            </SH2>
            <XStack className="flex-1 items-center justify-between">
              {creditScoreInfoListData?.map(({ id, title, icon }) => (
                <Stack
                  className="items-center justify-center"
                  style={{ width: '33%' }}
                  key={id}
                >
                  {icon}
                  <B4 className="mt-4/2 text-grays-60 w-[100] flex-1 text-center">
                    {title}
                  </B4>
                </Stack>
              ))}
            </XStack>
            <Pressable
              eventName="click_action"
              eventPayload={{
                item_name: 'homepage_how_new_credit_score_works',
              }}
              className="border-grays-40 mt-3 w-[250] flex-row items-center justify-center space-x-1 self-center rounded-full border py-2"
              onPress={handlePlayVideo}
            >
              <Stack>
                <Icon type="VideoOutlined" color={colors.primary[100]} />
              </Stack>
              <BTN className="text-primary-100 text-center">
                {i18n.t('checkCreditScore.whyScore')}
              </BTN>
            </Pressable>

            {/*----Share Credit Score CTA Section----*/}
            <YStack className="mx-4 my-4">
              <Pressable
                className={
                  'bg-primary-100 py-4/2 flex-row items-center justify-center gap-x-1 rounded'
                }
                fullWidth
                onPress={handleShare}
              >
                <Icon type="Share" color={colors.neutral.white100} />
                <BTN className="text-center text-white">
                  {i18n.t('checkCreditScore.shareLinkCTA')}
                </BTN>
              </Pressable>
            </YStack>
          </Stack>
        </YStack>
      </YStack>
    </BottomSheet>
  )
}

export default CreditScoreBottomSheet
